import { useEffect, useRef, useState } from "react";
import { T, base, injectGlobalStyles } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { useGoogleLogin } from "@react-oauth/google";

// ── Password strength calculator ──────────────────────────────────
const getStrength = (pw) => {
  if (!pw) return { score: 0, label: "", color: "transparent", bars: 0 };
  let score = 0;
  if (pw.length >= 8)                  score++;
  if (pw.length >= 12)                 score++;
  if (/[A-Z]/.test(pw))               score++;
  if (/[0-9]/.test(pw))               score++;
  if (/[^A-Za-z0-9]/.test(pw))        score++;
  if (score <= 1) return { score, label: "Too weak",   color: T.rose,    bars: 1 };
  if (score === 2) return { score, label: "Weak",      color: T.amber,   bars: 2 };
  if (score === 3) return { score, label: "Fair",      color: T.amber,   bars: 3 };
  if (score === 4) return { score, label: "Strong",    color: T.emerald, bars: 4 };
  return               { score, label: "Very strong", color: T.sky,     bars: 5 };
};

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// ── Icons ──────────────────────────────────────────────────────────
const EyeIcon = ({ open }) => open
  ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;

const CheckMini = () =>
  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>;

// ── Animated background ────────────────────────────────────────────
const Background = () => (
  <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
    <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.04 }}>
      <defs>
        <pattern id="g" width="48" height="48" patternUnits="userSpaceOnUse">
          <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#6366f1" strokeWidth="0.7"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#g)"/>
    </svg>
    <div style={{ position:"absolute", width:520, height:520, borderRadius:"50%", background:"radial-gradient(circle, rgba(99,102,241,0.11) 0%, transparent 65%)", top:"15%", left:"50%", transform:"translateX(-50%)" }} />
    <div style={{ position:"absolute", width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle, rgba(56,189,248,0.07) 0%, transparent 65%)", bottom:"10%", left:"10%" }} />
    <div style={{ position:"absolute", width:240, height:240, borderRadius:"50%", background:"radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 65%)", top:"5%", right:"10%" }} />
    {[12,28,45,62,78,90].map((l,i) => (
      <div key={i} style={{ position:"absolute", width:4, height:4, borderRadius:"50%", background:T.emerald, opacity:0.25, left:`${l}%`, top:`${[20,70,38,82,14,52][i]}%`, animation:`blink ${2+i*0.35}s ease-in-out infinite`, animationDelay:`${i*0.28}s` }} />
    ))}
  </div>
);

// ── Main component ─────────────────────────────────────────────────
const AuthScreen = ({ onLogin }) => {
  const [mode,          setMode]          = useState("login");
  const [email,         setEmail]         = useState("");
  const [password,      setPassword]      = useState("");
  const [confirmPw,     setConfirmPw]     = useState("");
  const [showPw,        setShowPw]        = useState(false);
  const [showConf,      setShowConf]      = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [success,       setSuccess]       = useState("");
  const [touched,       setTouched]       = useState({ email:false, password:false, confirm:false });
  const [attempts,      setAttempts]      = useState(0);
  const [lockEnd,       setLockEnd]       = useState(null);
  const [lockSecs,      setLockSecs]      = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleHover,   setGoogleHover]   = useState(false);
  const emailRef = useRef(null);

  const triggerGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true); setError("");
      try {
        const data = await apiFetch("/api/auth/google", {
          method: "POST",
          body: JSON.stringify({ access_token: tokenResponse.access_token }),
        });
        if (data.token) {
          localStorage.setItem("token", data.token);
          onLogin(data.token);
        } else if (data.code === "PENDING_VERIFICATION") {
          setSuccess("Account created! Please check your inbox and click the verification link to activate your account.");
        } else {
          setError(data.error || "Google sign-in failed");
        }
      } catch { setError("Google sign-in failed — please try again"); }
      finally { setGoogleLoading(false); }
    },
    onError: () => { setGoogleLoading(false); setError("Google sign-in was cancelled or failed"); },
  });

  useEffect(() => { injectGlobalStyles(); emailRef.current?.focus(); }, []);

  // lockout timer
  useEffect(() => {
    if (!lockEnd) return;
    const t = setInterval(() => {
      const r = Math.ceil((lockEnd - Date.now()) / 1000);
      if (r <= 0) { setLockEnd(null); setLockSecs(0); }
      else setLockSecs(r);
    }, 400);
    return () => clearInterval(t);
  }, [lockEnd]);

  const isLocked  = lockEnd && Date.now() < lockEnd;
  const strength  = getStrength(password);
  const emailErr  = touched.email    && email    && !isValidEmail(email)       ? "Enter a valid email" : "";
  const pwWeak    = mode==="signup"  && touched.password && password && strength.score < 3;
  const confErr   = mode==="signup"  && touched.confirm  && confirmPw && confirmPw !== password ? "Passwords don't match" : "";

  const canSubmit = !loading && !isLocked && email && password &&
    (mode==="login" || (isValidEmail(email) && strength.score >= 3 && password === confirmPw));

  const submit = async () => {
    setTouched({ email:true, password:true, confirm:true });
    if (!canSubmit) return;
    setLoading(true); setError(""); setSuccess("");
    try {
      const body = { email: email.trim().toLowerCase(), password };
      const data = await apiFetch(`/api/${mode}`, { method:"POST", body: JSON.stringify(body) });

      if (mode === "login") {
        if (data.token) {
          setAttempts(0);
          localStorage.setItem("token", data.token);
          onLogin(data.token);
        } else if (data.code === "PENDING_VERIFICATION" || data.code === "PENDING_APPROVAL") {
          setSuccess("Please check your email and click the verification link to activate your account.");
        } else if (data.code === "BANNED") {
          setError(data.error);
        } else {
          const next = attempts + 1;
          setAttempts(next);
          if (next >= 5) { setLockEnd(Date.now() + 30_000); setError("Too many attempts. Locked for 30s."); }
          else           { setError(`${data.error || "Invalid credentials"}${next >= 3 ? ` · ${5-next} attempt${5-next!==1?"s":""} left` : ""}`); }
        }
      } else {
        if (data.success) {
          if (data.pending) {
            setSuccess("Account created! Check your email and click the verification link to activate your account.");
          } else {
            setSuccess("Account created! Signing you in...");
            const login = await apiFetch("/api/login", { method:"POST", body: JSON.stringify(body) });
            if (login.token) { localStorage.setItem("token", login.token); setTimeout(() => onLogin(login.token), 900); }
            else             { setMode("login"); setSuccess("Account created! Sign in now."); }
          }
        } else {
          setError(data.error || "Signup failed");
        }
      }
    } catch { setError("Network error — check your connection"); }
    setLoading(false);
  };

  const switchMode = () => {
    setMode(m => m==="login" ? "signup" : "login");
    setError(""); setSuccess(""); setPassword(""); setConfirmPw("");
    setTouched({ email:false, password:false, confirm:false });
  };

  // ── Field style helpers ─────────────────────────────────────────
  const base_input = {
    width:"100%", boxSizing:"border-box", padding:"11px 14px",
    background:T.bg0, borderRadius:10, color:T.text1, fontSize:13,
    outline:"none", fontFamily:base.font, transition:"border-color 0.2s, box-shadow 0.2s",
  };
  const borderedInput = (hasErr, hasOk) => ({
    ...base_input,
    border: `1px solid ${hasErr ? T.rose : hasOk ? T.emerald : T.border2}`,
    boxShadow: hasErr ? "0 0 0 3px rgba(244,63,94,0.08)"
             : hasOk  ? "0 0 0 3px rgba(99,102,241,0.08)"
             : "none",
  });

  const Label = ({ children }) => (
    <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:7, letterSpacing:"0.06em", textTransform:"uppercase" }}>
      {children}
    </label>
  );

  const FieldError = ({ msg }) => msg
    ? <div style={{ fontSize:11, color:T.rose, marginTop:5 }}>{msg}</div>
    : null;

  const PwToggle = ({ show, toggle }) => (
    <button onClick={toggle} type="button" style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:T.text3, display:"flex", padding:2 }}>
      <EyeIcon open={show} />
    </button>
  );

  // ── Password requirements list ──────────────────────────────────
  const pwReqs = [
    { label:"8+ characters",    ok: password.length >= 8         },
    { label:"Uppercase letter", ok: /[A-Z]/.test(password)        },
    { label:"Number",           ok: /[0-9]/.test(password)         },
    { label:"Special character",ok: /[^A-Za-z0-9]/.test(password)  },
  ];

  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:T.bg0, fontFamily:base.font, position:"relative", overflow:"hidden" }}>
      <Background />

      <div style={{ width:"100%", maxWidth:430, position:"relative", zIndex:1, animation:"fadeSlideIn 0.45s cubic-bezier(0.16,1,0.3,1)", padding:"0 16px" }}>

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <img src="/logo.svg" alt="Zepliq" style={{ width:72, height:72, objectFit:"contain", margin:"0 auto 14px", display:"block" }} />
          <div style={{ fontSize:27, fontWeight:800, color:T.text1, letterSpacing:"-0.04em" }}>
            Zepliq
          </div>
          <div style={{ fontSize:13, color:T.text3, marginTop:4 }}>
            {mode==="login" ? "Sign in to your dashboard" : "Create your free account"}
          </div>
        </div>

        {/* Card */}
        <div style={{ background:T.bg1, borderRadius:22, border:`1px solid ${T.border2}`, padding:"28px 28px 22px", boxShadow:"0 24px 60px rgba(0,0,0,0.45),0 0 0 1px rgba(255,255,255,0.03)" }}>

          {/* Mode tabs */}
          <div style={{ display:"flex", gap:4, background:T.bg0, borderRadius:11, padding:4, marginBottom:24, border:`1px solid ${T.border1}` }}>
            {["login","signup"].map(m => (
              <button key={m} onClick={mode!==m ? switchMode : undefined} style={{ flex:1, padding:"8px 0", borderRadius:8, border:"none", cursor:"pointer", fontFamily:base.font, fontSize:13, fontWeight:600, transition:"all 0.2s", background:mode===m ? "linear-gradient(135deg,#6366f1,#8b5cf6)" : "transparent", color:mode===m ? "#fff" : T.text3 }}>
                {m==="login" ? "Sign in" : "Sign up"}
              </button>
            ))}
          </div>

          {/* Success */}
          {success && (
            <div style={{ display:"flex", alignItems:"center", gap:9, padding:"11px 14px", borderRadius:10, marginBottom:18, background:T.emeraldDim, color:T.emerald, border:`1px solid rgba(99,102,241,0.25)`, fontSize:13, fontWeight:500, animation:"fadeSlideIn 0.3s ease" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              {success}
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ display:"flex", alignItems:"flex-start", gap:9, padding:"11px 14px", borderRadius:10, marginBottom:18, background:T.roseDim, color:T.rose, border:`1px solid rgba(244,63,94,0.25)`, fontSize:13, animation:"fadeSlideIn 0.3s ease" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink:0, marginTop:1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {error}
            </div>
          )}

          {/* Lockout bar */}
          {isLocked && (
            <div style={{ padding:"10px 14px", borderRadius:10, marginBottom:18, background:"rgba(245,158,11,0.1)", border:`1px solid rgba(245,158,11,0.25)`, fontSize:12, color:T.amber, display:"flex", alignItems:"center", gap:8 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Locked — try again in <strong style={{ fontFamily:"'JetBrains Mono',monospace" }}>{lockSecs}s</strong>
              <div style={{ flex:1, height:3, background:"rgba(245,158,11,0.2)", borderRadius:2, overflow:"hidden" }}>
                <div style={{ height:"100%", background:T.amber, borderRadius:2, width:`${(lockSecs/30)*100}%`, transition:"width 0.4s linear" }} />
              </div>
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom:16 }}>
            <Label>Email address</Label>
            <input ref={emailRef} type="email" value={email} placeholder="you@example.com"
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key==="Enter" && submit()}
              style={borderedInput(!!emailErr, touched.email && isValidEmail(email))}
              onFocus={e => { e.target.style.borderColor=T.emerald; e.target.style.boxShadow="0 0 0 3px rgba(99,102,241,0.1)"; }}
              onBlur={e  => { setTouched(t => ({...t, email:true})); e.target.style.borderColor=emailErr?T.rose:isValidEmail(email)?T.emerald:T.border2; e.target.style.boxShadow=emailErr?"0 0 0 3px rgba(244,63,94,0.08)":isValidEmail(email)?"0 0 0 3px rgba(99,102,241,0.08)":"none"; }}
            />
            <FieldError msg={emailErr} />
          </div>

          {/* Password */}
          <div style={{ marginBottom:16 }}>
            <Label>Password</Label>
            <div style={{ position:"relative" }}>
              <input type={showPw?"text":"password"} value={password}
                placeholder={mode==="signup" ? "Min 8 chars, uppercase, number, symbol" : "••••••••"}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key==="Enter" && submit()}
                style={{ ...borderedInput(pwWeak, mode==="signup"&&touched.password&&strength.score>=3), paddingRight:42 }}
                onFocus={e => { e.target.style.borderColor=T.emerald; e.target.style.boxShadow="0 0 0 3px rgba(99,102,241,0.1)"; }}
                onBlur={e  => { setTouched(t => ({...t, password:true})); const hasErr=mode==="signup"&&password&&strength.score<3; const hasOk=mode==="signup"&&strength.score>=3; e.target.style.borderColor=hasErr?T.rose:hasOk?T.emerald:T.border2; e.target.style.boxShadow=hasErr?"0 0 0 3px rgba(244,63,94,0.08)":hasOk?"0 0 0 3px rgba(99,102,241,0.08)":"none"; }}
              />
              <PwToggle show={showPw} toggle={() => setShowPw(v=>!v)} />
            </div>

            {/* Strength bars */}
            {mode==="signup" && password && (
              <div style={{ marginTop:8 }}>
                <div style={{ display:"flex", gap:3, marginBottom:4 }}>
                  {[1,2,3,4,5].map(i => (
                    <div key={i} style={{ flex:1, height:3, borderRadius:2, transition:"background 0.3s", background: i<=strength.bars ? strength.color : T.border2 }} />
                  ))}
                </div>
                <div style={{ fontSize:11, color:strength.color, fontWeight:500 }}>
                  {strength.label}
                  {strength.score < 3 && <span style={{ color:T.text3, fontWeight:400 }}>{" — needs more complexity"}</span>}
                </div>
              </div>
            )}
            {pwWeak && <FieldError msg="Password is too weak" />}
          </div>

          {/* Confirm password */}
          {mode==="signup" && (
            <div style={{ marginBottom:16, animation:"fadeSlideIn 0.25s ease" }}>
              <Label>Confirm password</Label>
              <div style={{ position:"relative" }}>
                <input type={showConf?"text":"password"} value={confirmPw}
                  placeholder="Re-enter your password"
                  onChange={e => setConfirmPw(e.target.value)}
                  onKeyDown={e => e.key==="Enter" && submit()}
                  style={{ ...borderedInput(!!confErr, touched.confirm&&confirmPw===password&&confirmPw.length>0), paddingRight:42 }}
                  onFocus={e => { e.target.style.borderColor=T.emerald; e.target.style.boxShadow="0 0 0 3px rgba(99,102,241,0.1)"; }}
                  onBlur={e  => { setTouched(t => ({...t, confirm:true})); e.target.style.borderColor=confErr?T.rose:confirmPw===password&&confirmPw?T.emerald:T.border2; e.target.style.boxShadow=confErr?"0 0 0 3px rgba(244,63,94,0.08)":confirmPw===password&&confirmPw?"0 0 0 3px rgba(99,102,241,0.08)":"none"; }}
                />
                <PwToggle show={showConf} toggle={() => setShowConf(v=>!v)} />
              </div>
              <FieldError msg={confErr} />
            </div>
          )}

          {/* Pw requirements checklist */}
          {mode==="signup" && password && (
            <div style={{ padding:"12px 14px", borderRadius:10, marginBottom:18, background:T.bg0, border:`1px solid ${T.border1}`, display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 12px", animation:"fadeSlideIn 0.25s ease" }}>
              {pwReqs.map(({ label, ok }) => (
                <div key={label} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:ok?T.emerald:T.text3, transition:"color 0.2s" }}>
                  <div style={{ width:14, height:14, borderRadius:"50%", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", background:ok?T.emeraldDim:T.border1, border:`1px solid ${ok?T.emerald:"transparent"}`, transition:"all 0.2s", color:T.emerald }}>
                    {ok && <CheckMini />}
                  </div>
                  {label}
                </div>
              ))}
            </div>
          )}

          {/* Submit */}
          <button onClick={submit} disabled={!canSubmit} style={{
            width:"100%", padding:"12px 0", borderRadius:10, border:"none",
            cursor: canSubmit ? "pointer" : "not-allowed",
            fontFamily:base.font, fontSize:14, fontWeight:700, letterSpacing:"0.01em",
            transition:"all 0.2s",
            background: canSubmit ? "linear-gradient(135deg,#6366f1,#4f46e5)" : "rgba(255,255,255,0.05)",
            color:      canSubmit ? "#fff" : T.text3,
            boxShadow:  canSubmit ? "0 4px 20px rgba(99,102,241,0.3)" : "none",
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
            transform: loading ? "scale(0.98)" : "scale(1)",
          }}>
            {loading ? (
              <>
                <div style={{ width:15, height:15, border:"2px solid rgba(255,255,255,0.25)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
                {mode==="login" ? "Signing in..." : "Creating account..."}
              </>
            ) : mode==="login" ? "Sign in to dashboard" : "Create account"}
          </button>

          <div style={{ display:"flex", alignItems:"center", gap:12, margin:"20px 0 16px" }}>
            <div style={{ flex:1, height:1, background:T.border1 }} />
            <span style={{ fontSize:11, color:T.text3 }}>or continue with</span>
            <div style={{ flex:1, height:1, background:T.border1 }} />
          </div>
          <button
            type="button"
            onClick={() => { if (!googleLoading) { setGoogleLoading(true); triggerGoogleLogin(); } }}
            onMouseEnter={() => setGoogleHover(true)}
            onMouseLeave={() => setGoogleHover(false)}
            disabled={googleLoading}
            style={{
              width: "100%", padding: "11px 0", borderRadius: 10, border: `1px solid ${googleHover && !googleLoading ? "rgba(99,102,241,0.5)" : T.border2}`,
              cursor: googleLoading ? "not-allowed" : "pointer",
              background: googleHover && !googleLoading ? "rgba(99,102,241,0.06)" : T.bg0,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              fontFamily: base.font, fontSize: 14, fontWeight: 600, color: googleLoading ? T.text3 : T.text1,
              transition: "all 0.2s", marginBottom: 16, opacity: googleLoading ? 0.65 : 1,
              boxShadow: googleHover && !googleLoading ? "0 0 0 3px rgba(99,102,241,0.08)" : "none",
            }}
          >
            {googleLoading ? (
              <>
                <div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.15)", borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                <span>Connecting to Google...</span>
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                <span>{mode === "login" ? "Continue with Google" : "Sign up with Google"}</span>
              </>
            )}
          </button>

          {/* Switch mode */}
          <div style={{ textAlign:"center", fontSize:13, color:T.text3 }}>
            {mode==="login" ? "No account? " : "Already have one? "}
            <span onClick={switchMode} style={{ color:T.emerald, cursor:"pointer", fontWeight:600 }}>
              {mode==="login" ? "Create one →" : "Sign in →"}
            </span>
          </div>
        </div>

        {/* Security footer */}
        <div style={{ textAlign:"center", marginTop:14, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <span style={{ fontSize:11, color:T.text3 }}>bcrypt hashed · JWT secured · Your data stays private</span>
        </div>

      </div>
    </div>
  );
};

export default AuthScreen;
